import React from 'react';
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  Chip,
  Tooltip,
  CircularProgress,
  Typography,
  TablePagination,
  InputAdornment,
  TextField,
  alpha,
  Button,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  AppBar,
  Toolbar,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FileClock, Search, Download, Users, User, GitCompare, ArrowLeft } from 'lucide-react';
import { BglPrimarySmallButton, BglPrimaryOutlinedSmallButton } from 'components/button/BglButtons';
import { SearchableMultiSelect } from 'components/common/SearchableMultiSelect';
import { useGlobalConfig } from 'context/GlobalContext';
import { createAuditApiClient } from 'framework/http/AuditClient';
import type { AuditEvent, GetAuditEventsParams, AuditEventTypeConfig } from 'types/apis/audit';
import { selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';

type ActivityMode = 'user' | 'project';
type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
const VALID_CHIP_COLORS = new Set<string>(['default', 'primary', 'secondary', 'error', 'info', 'success', 'warning']);

const PER_PAGE = 50;

function toDateString(d: Date): string {
  return d.toISOString().split('T')[0];
}

function defaultDateRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 7);
  return { from, to };
}

function truncate(val: string | null, max = 80): string {
  if (!val) return '—';
  return val.length > max ? val.slice(0, max) + '…' : val;
}

function toDisplayLabel(val: string): string {
  return val.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function tryParseJson(val: string | null): Record<string, unknown> | null {
  if (!val) return null;
  try {
    let parsed: unknown = JSON.parse(val);
    if (typeof parsed === 'string') {
      try { parsed = JSON.parse(parsed); } catch {}
    }
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {}
  return null;
}

function getJsonDiffKeys(oldObj: Record<string, unknown>, newObj: Record<string, unknown>): string[] {
  const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);
  return [...allKeys].filter(k => JSON.stringify(oldObj[k]) !== JSON.stringify(newObj[k]));
}

function tryFormatAsJson(val: string | null): string | null {
  if (!val) return null;
  try {
    let parsed: unknown = JSON.parse(val);
    if (typeof parsed === 'string') {
      try { parsed = JSON.parse(parsed); } catch {}
    }
    if (typeof parsed !== 'undefined') return JSON.stringify(parsed, null, 2);
  } catch {}
  return null;
}

function renderHighlightedJson(
  obj: Record<string, unknown>,
  highlightKeys: string[],
  highlightColor: 'error' | 'success'
): React.ReactNode {
  const lines = JSON.stringify(obj, null, 2).split('\n');
  return lines.map((line, i) => {
    const isHighlighted = highlightKeys.some(key => line.includes(`"${key}"`));
    return (
      <Box
        key={i}
        component="span"
        sx={{
          display: 'block',
          bgcolor: isHighlighted
            ? (theme: any) => alpha(theme.palette[highlightColor].main, 0.25)
            : 'transparent',
          mx: -1.5,
          px: 1.5,
        }}
      >
        {line}
      </Box>
    );
  });
}

function exportCsv(events: AuditEvent[]) {
  const escape = (v: string | null) => `"${(v || '').replace(/"/g, '""')}"`;
  const header = ['User', 'Timestamp', 'Action', 'Source', 'Entity Key', 'Old Value', 'New Value'].join(',');
  const rows = events.map(e =>
    [
      escape(e.user_email),
      escape(e.created_at),
      escape(e.event_type),
      escape(e.source_name),
      escape(e.entity_key),
      escape(e.old_value),
      escape(e.new_value),
    ].join(',')
  );
  const blob = new Blob([header + '\n' + rows.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `audit_trail_${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export const AuditTrailPage: React.FC = () => {
  const { rc } = useGlobalConfig();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reduxProjectId = useSelector(selectSelectedPilotId);

  const urlProjectId = searchParams.get('projectId') || '';
  const rawSource = searchParams.get('source');

  const SOURCE_DISPLAY_NAMES: Record<string, string> = {
    cx_editor: 'CX Editor',
    cms: 'CMS',
  };
  const backLabel = rawSource
    ? `Back to ${SOURCE_DISPLAY_NAMES[rawSource] ?? toDisplayLabel(rawSource)}`
    : 'Back';

  const initialProjectId = urlProjectId || reduxProjectId || '';

  const [projectIdInput, setProjectIdInput] = React.useState(initialProjectId);
  const [pilotId, setPilotId] = React.useState(initialProjectId);

  const [activityMode, setActivityMode] = React.useState<ActivityMode>('user');
  const [events, setEvents] = React.useState<AuditEvent[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);

  const [searchTerm, setSearchTerm] = React.useState('');
  const [eventTypeFilter, setEventTypeFilter] = React.useState<string[]>([]);
  const [sourceFilter, setSourceFilter] = React.useState<string[]>(rawSource ? [rawSource] : []);
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('desc');
  const [dateRange, setDateRange] = React.useState<{ from: Date; to: Date }>(defaultDateRange);
  const [availableEventTypes, setAvailableEventTypes] = React.useState<string[]>([]);
  const [availableSources, setAvailableSources] = React.useState<string[]>([]);
  const [compareEvent, setCompareEvent] = React.useState<AuditEvent | null>(null);
  const [eventTypeConfigs, setEventTypeConfigs] = React.useState<Record<string, AuditEventTypeConfig>>({});

  React.useEffect(() => {
    if (!pilotId) return;
    (async () => {
      try {
        const api = createAuditApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
        const sources = await api.getAuditSources({ project_id: pilotId });
        setAvailableSources(sources);
      } catch {}
    })();
  }, [pilotId, rc]);

  React.useEffect(() => {
    if (!pilotId) return;
    (async () => {
      try {
        const api = createAuditApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
        const params: { project_id: string; source_names?: string } = { project_id: pilotId };
        if (sourceFilter.length > 0) params.source_names = sourceFilter.join(',');
        const configs: AuditEventTypeConfig[] = await api.getAuditEventTypes(params);
        setAvailableEventTypes(configs.map(c => c.event_type));
        const map: Record<string, AuditEventTypeConfig> = {};
        for (const c of configs) map[c.event_type] = c;
        setEventTypeConfigs(map);
        setEventTypeFilter([]);
      } catch {}
    })();
  }, [pilotId, rc, sourceFilter]);

  React.useEffect(() => {
    if (!pilotId) return;
    let ignore = false;
    const controller = new AbortController();

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const api = createAuditApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
        const params: GetAuditEventsParams = {
          project_id: pilotId,
          page: page + 1,
          per_page: PER_PAGE,
          from_date: toDateString(dateRange.from),
          to_date: toDateString(dateRange.to),
        };
        if (sourceFilter.length > 0) params.source_names = sourceFilter.join(',');
        if (eventTypeFilter.length > 0) params.event_type = eventTypeFilter.join(',');
        const data = activityMode === 'user'
          ? await api.getUserAuditEvents(params)
          : await api.getProjectAuditEvents(params);
        if (!ignore) {
          setEvents(data.data || []);
          setTotal(data.total || 0);
        }
      } catch {
        if (!ignore) setError('Failed to load audit events');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();

    return () => { ignore = true; controller.abort(); };
  }, [pilotId, page, eventTypeFilter, sourceFilter, dateRange, rc, activityMode]);

  React.useEffect(() => {
    setPage(0);
  }, [eventTypeFilter, sourceFilter, dateRange, activityMode, pilotId]);

  const filteredEvents = React.useMemo(() => {
    const term = searchTerm.toLowerCase();
    const filtered = term
      ? events.filter(e =>
          (e.user_email || '').toLowerCase().includes(term) ||
          (e.event_type || '').toLowerCase().includes(term) ||
          (e.entity_key || '').toLowerCase().includes(term) ||
          (e.old_value || '').toLowerCase().includes(term) ||
          (e.new_value || '').toLowerCase().includes(term)
        )
      : events;
    return [...filtered].sort((a, b) => {
      const cmp = a.created_at.localeCompare(b.created_at);
      return sortDirection === 'desc' ? -cmp : cmp;
    });
  }, [events, searchTerm, sortDirection]);

  const sourceDisplayOptions = availableSources.map(toDisplayLabel);
  const eventTypeDisplayOptions = availableEventTypes.map(toDisplayLabel);
  const sourceRawByDisplay = Object.fromEntries(availableSources.map(s => [toDisplayLabel(s), s]));
  const eventTypeRawByDisplay = Object.fromEntries(availableEventTypes.map(t => [toDisplayLabel(t), t]));
  const sourceFilterDisplay = sourceFilter.map(toDisplayLabel);
  const eventTypeFilterDisplay = eventTypeFilter.map(toDisplayLabel);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <AppBar position="sticky" color="default" elevation={1} sx={{ bgcolor: 'background.paper' }}>
          <Toolbar sx={{ gap: 1.5 }}>
            <Button
              startIcon={<ArrowLeft size={16} />}
              onClick={() => navigate(-1)}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap', fontWeight: 500 }}
            >
              {backLabel}
            </Button>
            
            <TextField
              size="small"
              label="Project ID"
              value={projectIdInput}
              onChange={e => setProjectIdInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') setPilotId(projectIdInput.trim()); }}
              sx={{ minWidth: 150, ml: 1 }}
              placeholder="Enter project ID…"
            />
            <Button
              size="small"
              variant="contained"
              onClick={() => setPilotId(projectIdInput.trim())}
              disabled={!projectIdInput.trim() || projectIdInput.trim() === pilotId}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              Load
            </Button>
            <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
              <BglPrimaryOutlinedSmallButton
                startIcon={<Download size={16} />}
                onClick={() => exportCsv(filteredEvents)}
                disabled={filteredEvents.length === 0}
              >
                Export CSV
              </BglPrimaryOutlinedSmallButton>
              {activityMode === 'user' ? (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Users size={14} />}
                  onClick={() => setActivityMode('project')}
                  sx={{ textTransform: 'none', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                >
                  View Project Activity
                </Button>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<User size={14} />}
                  onClick={() => setActivityMode('user')}
                  sx={{ textTransform: 'none', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                >
                  View My Activity
                </Button>
              )}
            </Box>
          </Toolbar>
        </AppBar>

        <Box sx={{ flex: 1, p: 3 }}>

          {/* Filters row */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search user, action, entity…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              sx={{ flex: 1, minWidth: 240 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={16} />
                  </InputAdornment>
                ),
              }}
            />

            <SearchableMultiSelect
              label="Sources"
              options={sourceDisplayOptions}
              selected={sourceFilterDisplay}
              onChange={labels => setSourceFilter(labels.map(l => sourceRawByDisplay[l] ?? l))}
              labelPosition="inline"
            />

            <SearchableMultiSelect
              label="Actions"
              options={eventTypeDisplayOptions}
              selected={eventTypeFilterDisplay}
              onChange={labels => setEventTypeFilter(labels.map(l => eventTypeRawByDisplay[l] ?? l))}
              labelPosition="inline"
            />

            <DatePicker
              label="From"
              value={dateRange.from}
              onChange={date => date && setDateRange(d => ({ ...d, from: date }))}
              maxDate={dateRange.to}
              slotProps={{ textField: { size: 'small' } }}
            />
            <DatePicker
              label="To"
              value={dateRange.to}
              onChange={date => date && setDateRange(d => ({ ...d, to: date }))}
              minDate={dateRange.from}
              maxDate={new Date()}
              slotProps={{ textField: { size: 'small' } }}
            />
          </Box>

          {!pilotId && (
            <Box sx={{ py: 8, textAlign: 'center', color: 'text.secondary' }}>
              <FileClock size={48} style={{ marginBottom: 12, opacity: 0.3 }} />
              <Typography variant="h6" color="text.secondary">Enter a Project ID to load audit events</Typography>
            </Box>
          )}

          {pilotId && (
            <>
              <Box sx={{ overflowX: 'auto' }}>
                {loading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={32} />
                  </Box>
                )}
                {error && (
                  <Typography color="error" sx={{ py: 2 }}>{error}</Typography>
                )}
                {!loading && !error && (
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ minWidth: 200 }}>User</TableCell>
                        <TableCell sx={{ minWidth: 180 }}>
                          <TableSortLabel
                            active
                            direction={sortDirection}
                            onClick={() => setSortDirection(d => (d === 'desc' ? 'asc' : 'desc'))}
                          >
                            Timestamp
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ minWidth: 140 }}>Source</TableCell>
                        <TableCell sx={{ minWidth: 160 }}>Action</TableCell>
                        <TableCell sx={{ minWidth: 220 }}>Entity</TableCell>
                        <TableCell sx={{ minWidth: 320 }}>Changes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredEvents.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                            No events found
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredEvents.map(event => (
                          <TableRow key={event.id} hover>
                            <TableCell>{event.user_email || '—'}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {new Date(event.created_at).toLocaleString()}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                {event.source_name || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const cfg = eventTypeConfigs[event.event_type];
                                const hasHex = !!cfg?.hex;
                                const chipColor = !hasHex && cfg?.color && VALID_CHIP_COLORS.has(cfg.color)
                                  ? (cfg.color as ChipColor)
                                  : 'default';
                                return (
                                  <Chip
                                    label={toDisplayLabel(event.event_type)}
                                    color={hasHex ? undefined : chipColor}
                                    size="small"
                                    sx={{ fontSize: '0.7rem', ...(hasHex && { bgcolor: cfg!.hex, color: '#fff' }) }}
                                  />
                                );
                              })()}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                {truncate(event.entity_key, 60)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const oldObj = tryParseJson(event.old_value);
                                const newObj = tryParseJson(event.new_value);
                                const isJson = !!(oldObj || newObj);
                                const allDiffKeys = oldObj && newObj
                                  ? getJsonDiffKeys(oldObj, newObj)
                                  : oldObj ? Object.keys(oldObj)
                                  : newObj ? Object.keys(newObj) : [];
                                const displayKeys = allDiffKeys.slice(0, 4);
                                const moreCount = allDiffKeys.length - displayKeys.length;

                                if (isJson) {
                                  return (
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        {allDiffKeys.length === 0 && (
                                          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>—</Typography>
                                        )}
                                        {displayKeys.map(key => (
                                          <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25, flexWrap: 'nowrap', overflow: 'hidden' }}>
                                            <Typography sx={{ fontSize: '0.68rem', fontFamily: 'monospace', fontWeight: 700, color: 'text.secondary', flexShrink: 0 }}>
                                              {key}:
                                            </Typography>
                                            {oldObj && key in oldObj && (
                                              <Typography sx={{ fontSize: '0.68rem', fontFamily: 'monospace', color: 'error.dark', bgcolor: (theme) => alpha(theme.palette.error.main, 0.12), px: 0.5, borderRadius: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 100, flexShrink: 1 }}>
                                                {truncate(JSON.stringify(oldObj[key]), 30)}
                                              </Typography>
                                            )}
                                            {oldObj && newObj && key in oldObj && key in newObj && (
                                              <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', flexShrink: 0 }}>→</Typography>
                                            )}
                                            {newObj && key in newObj && (
                                              <Typography sx={{ fontSize: '0.68rem', fontFamily: 'monospace', color: 'success.dark', bgcolor: (theme) => alpha(theme.palette.success.main, 0.12), px: 0.5, borderRadius: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 100, flexShrink: 1 }}>
                                                {truncate(JSON.stringify(newObj[key]), 30)}
                                              </Typography>
                                            )}
                                          </Box>
                                        ))}
                                        {moreCount > 0 && (
                                          <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', mt: 0.25 }}>
                                            +{moreCount} more
                                          </Typography>
                                        )}
                                      </Box>
                                      <Tooltip title="Compare full JSON">
                                        <IconButton size="small" onClick={() => setCompareEvent(event)} sx={{ flexShrink: 0, mt: 0.25 }}>
                                          <GitCompare size={14} />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  );
                                }

                                return (
                                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      {event.old_value && (
                                        <Tooltip title={event.old_value.length > 80 ? event.old_value : ''} placement="left">
                                          <Box sx={{ bgcolor: (theme) => alpha(theme.palette.error.main, 0.1), borderLeft: '3px solid', borderLeftColor: 'error.main', px: 0.75, py: 0.25, mb: event.new_value ? 0.5 : 0, borderRadius: '2px' }}>
                                            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'error.dark', fontFamily: 'monospace' }}>
                                              {truncate(event.old_value, 80)}
                                            </Typography>
                                          </Box>
                                        </Tooltip>
                                      )}
                                      {event.new_value && (
                                        <Tooltip title={event.new_value.length > 80 ? event.new_value : ''} placement="left">
                                          <Box sx={{ bgcolor: (theme) => alpha(theme.palette.success.main, 0.1), borderLeft: '3px solid', borderLeftColor: 'success.main', px: 0.75, py: 0.25, borderRadius: '2px' }}>
                                            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'success.dark', fontFamily: 'monospace' }}>
                                              {truncate(event.new_value, 80)}
                                            </Typography>
                                          </Box>
                                        </Tooltip>
                                      )}
                                      {!event.old_value && !event.new_value && (
                                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>—</Typography>
                                      )}
                                    </Box>
                                    {(event.old_value || event.new_value) && (
                                      <Tooltip title="View full changes">
                                        <IconButton size="small" onClick={() => setCompareEvent(event)} sx={{ flexShrink: 0 }}>
                                          <GitCompare size={14} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>
                                );
                              })()}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                )}
              </Box>

              {!loading && !error && (
                <TablePagination
                  component="div"
                  count={total}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  rowsPerPage={PER_PAGE}
                  rowsPerPageOptions={[PER_PAGE]}
                />
              )}
            </>
          )}
        </Box>
      </Box>

      {/* JSON Compare Dialog */}
      <Dialog open={!!compareEvent} onClose={() => setCompareEvent(null)} fullWidth maxWidth="lg">
        <AppBar position="static" color="default" elevation={0} sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Toolbar sx={{ gap: 1 }} variant="dense">
            <GitCompare size={18} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Change Details</Typography>
            {compareEvent && (
              <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                {toDisplayLabel(compareEvent.event_type)} by {compareEvent.user_email || '—'} — {new Date(compareEvent.created_at).toLocaleString()}
              </Typography>
            )}
          </Toolbar>
        </AppBar>
        <DialogContent dividers>
          {compareEvent && (() => {
            const oldObj = tryParseJson(compareEvent.old_value);
            const newObj = tryParseJson(compareEvent.new_value);
            const oldFormatted = oldObj ? null : tryFormatAsJson(compareEvent.old_value);
            const newFormatted = newObj ? null : tryFormatAsJson(compareEvent.new_value);
            const isAnyJson = !!(oldObj || newObj || oldFormatted || newFormatted);
            const diffKeys = oldObj && newObj ? getJsonDiffKeys(oldObj, newObj) : [];

            if (isAnyJson) {
              return (
                <>
                  {diffKeys.length > 0 && (
                    <Box sx={{ mb: 2, px: 1.5, py: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontFamily: 'monospace' }}>
                        CHANGED: {diffKeys.join(' · ')}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: 'error.main' }}>Before</Typography>
                      <Box component="pre" sx={{ bgcolor: (theme) => alpha(theme.palette.error.main, 0.05), border: '1px solid', borderColor: (theme) => alpha(theme.palette.error.main, 0.2), borderRadius: 1, p: 1.5, fontSize: '0.72rem', fontFamily: 'monospace', overflow: 'auto', maxHeight: '60vh', m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {oldObj ? renderHighlightedJson(oldObj, diffKeys, 'error') : (oldFormatted || compareEvent.old_value || '—')}
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: 'success.main' }}>After</Typography>
                      <Box component="pre" sx={{ bgcolor: (theme) => alpha(theme.palette.success.main, 0.05), border: '1px solid', borderColor: (theme) => alpha(theme.palette.success.main, 0.2), borderRadius: 1, p: 1.5, fontSize: '0.72rem', fontFamily: 'monospace', overflow: 'auto', maxHeight: '60vh', m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {newObj ? renderHighlightedJson(newObj, diffKeys, 'success') : (newFormatted || compareEvent.new_value || '—')}
                      </Box>
                    </Box>
                  </Box>
                </>
              );
            }
            return (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'error.main' }}>Before</Typography>
                  <Box sx={{ bgcolor: (theme) => alpha(theme.palette.error.main, 0.05), border: '1px solid', borderColor: (theme) => alpha(theme.palette.error.main, 0.2), borderRadius: 1, p: 1.5, fontSize: '0.75rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {compareEvent.old_value || '—'}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'success.main' }}>After</Typography>
                  <Box sx={{ bgcolor: (theme) => alpha(theme.palette.success.main, 0.05), border: '1px solid', borderColor: (theme) => alpha(theme.palette.success.main, 0.2), borderRadius: 1, p: 1.5, fontSize: '0.75rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {compareEvent.new_value || '—'}
                  </Box>
                </Box>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <BglPrimarySmallButton onClick={() => setCompareEvent(null)}>Close</BglPrimarySmallButton>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default AuditTrailPage;
